import PropTypes from 'prop-types';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
// next
import Script from 'next/script';
// @mui
import { Box } from '@mui/material';
// sentry
import * as Sentry from '@sentry/nextjs';

// ----------------------------------------------------------------------

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

// Managed 모드에서 체크박스가 나타났을 때의 위젯 높이. 미리 확보해 레이아웃이 밀리지 않게 한다.
const WIDGET_HEIGHT = 65;

// window.turnstile 준비 대기.
// 10초가 지나면 일단 실패로 알리되, 느린 회선에서 스크립트가 늦게 도착할 수 있으므로 폴링은 계속한다.
const POLL_INTERVAL = 100;
const POLL_REPORT_AFTER = 10000;
const POLL_GIVE_UP_AFTER = 60000;

// 위젯을 못 쓰게 된 원인. Sentry 에서 이 태그로 실제 발생 빈도를 집계한다.
const REASON = {
  NO_SITE_KEY: 'no_site_key',
  SCRIPT_LOAD_ERROR: 'script_load_error',
  SCRIPT_TIMEOUT: 'script_timeout',
  RENDER_ERROR: 'render_error',
  CHALLENGE_ERROR: 'challenge_error',
};

// ----------------------------------------------------------------------

const Turnstile = forwardRef(function Turnstile({ onVerify, onExpire, onError }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const pollTimerRef = useRef(null);
  const failedRef = useRef(false);

  // 콜백은 ref 로 넘긴다. 부모가 리렌더될 때마다 위젯을 다시 그리면 안 되므로 render 는 마운트 시 한 번만 한다.
  const handlersRef = useRef();
  handlersRef.current = { onVerify, onExpire, onError };

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // 실패는 한 번만 보고한다. 스크립트 onError 와 폴링 타임아웃이 겹쳐 두 번 올라가는 것을 막는다.
  const reportFailure = useCallback((reason, code) => {
    if (failedRef.current) return;
    failedRef.current = true;
    Sentry.captureMessage(`turnstile unavailable: ${reason}`, {
      level: 'warning',
      tags: { turnstile_reason: reason },
      extra: { code },
    });
    handlersRef.current.onError();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }),
    []
  );

  useEffect(() => {
    if (!SITE_KEY) {
      reportFailure(REASON.NO_SITE_KEY);
      return undefined;
    }

    let waited = 0;
    let timeoutReported = false;

    const renderWidget = () => {
      if (widgetIdRef.current !== null || !containerRef.current) return;
      try {
        // 암묵적 렌더(class="cf-turnstile")는 스크립트 로드 시점에 한 번만 DOM 을 훑기 때문에
        // SPA 로 가입 페이지에 다시 들어오면 위젯이 뜨지 않는다. 그래서 explicit 렌더를 쓴다.
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => {
            // 재시도로 복구된 경우. 다음 실패도 다시 보고되도록 플래그를 푼다.
            failedRef.current = false;
            handlersRef.current.onVerify(token);
          },
          'expired-callback': () => handlersRef.current.onExpire(),
          'error-callback': (code) => reportFailure(REASON.CHALLENGE_ERROR, code),
        });
      } catch (error) {
        // render 가 throw 하면 콜백이 하나도 안 붙어 버튼이 영영 잠긴다. 실패로 처리해 빠져나간다.
        reportFailure(REASON.RENDER_ERROR, error?.message);
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      pollTimerRef.current = setInterval(() => {
        if (window.turnstile) {
          stopPolling();
          renderWidget();
          return;
        }
        waited += POLL_INTERVAL;
        if (waited >= POLL_REPORT_AFTER && !timeoutReported) {
          timeoutReported = true;
          reportFailure(REASON.SCRIPT_TIMEOUT);
        }
        if (waited >= POLL_GIVE_UP_AFTER) {
          stopPolling();
        }
      }, POLL_INTERVAL);
    }

    return () => {
      stopPolling();
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [reportFailure, stopPolling]);

  return (
    <>
      <Script
        src={SCRIPT_SRC}
        strategy="afterInteractive"
        onError={() => {
          // 로드가 확정적으로 실패했으므로 더 기다릴 이유가 없다.
          stopPolling();
          reportFailure(REASON.SCRIPT_LOAD_ERROR);
        }}
      />
      <Box ref={containerRef} sx={{ minHeight: WIDGET_HEIGHT }} />
    </>
  );
});

Turnstile.propTypes = {
  onVerify: PropTypes.func.isRequired,
  onExpire: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default Turnstile;
